
import { useState, useEffect } from 'react';
import { Container, Paper, Typography, Stepper, Step, StepLabel, CircularProgress, Alert } from '@mui/material';
import UserProfileForm from '../components/core/UserProfileForm';
import { useAuth } from '../contexts/AuthContext';
import { getUserProfile, saveUserProfile } from '../services/firestoreService';
import { UserProfile } from '../types';

const steps = ['Your Profile'];

export default function RegistrationPage() {
  const [activeStep] = useState(0);
  const { user, loading } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading || !user) {
      return;
    }

    const maxRetries = 10; // Increased from 5 to 10
    const delay = 2000; // Increased from 1000ms to 2000ms
    let attempt = 0;

    const profilePoller = setInterval(async () => {
      attempt++;
      console.log(`Checking for user profile, attempt ${attempt}...`);
      
      const profile = await getUserProfile(user.uid);
      if (profile) {
        clearInterval(profilePoller);
        console.log('Profile found, proceeding.');
        
        if (profile.profileComplete) {
          window.location.href = '/';
        } else {
          setUserProfile(profile);
        }
      } else if (attempt >= maxRetries) {
        clearInterval(profilePoller);
        console.error("User profile not found after multiple attempts...");
        setError("There was a problem retrieving your user profile. This can happen due to high server load. Please try logging out and logging back in.");
      }
    }, delay);

    return () => clearInterval(profilePoller);

  }, [user, loading]);

  const handleRegistrationComplete = async () => {
    if (!user) return;
    
    try {
        await saveUserProfile(user.uid, { profileComplete: true });
        window.location.href = '/';
    } catch (err) {
        console.error("Failed to mark profile as complete.", err);
        setError("There was an issue finalizing your registration. Please try again.");
    }
  };
  
  if (error) {
    return (
        <Container component="main" maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
            <Alert severity="error">{error}</Alert>
        </Container>
    );
  }

  if (!userProfile) {
    return (
      <Container component="main" maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{mt: 2}}>Finalizing account setup...</Typography>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="md" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography component="h1" variant="h4" align="center">
          Welcome to Synapse
        </Typography>
        <Typography component="p" variant="body1" align="center" sx={{ mb: 3 }}>
          Let's get your account set up.
        </Typography>
        <Stepper activeStep={activeStep} sx={{ pt: 3, pb: 5 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {activeStep === 0 && <UserProfileForm onNext={handleRegistrationComplete} />}
      </Paper>
    </Container>
  );
}

